/**
 * Página final post-pago del regalo de cumpleaños (/r/cumple).
 * Destino tras MercadoPago o Stripe: agradecimiento + promesa de sorpresa.
 */

import { Button } from "@/components/ui/button";
import { useSEO } from "@/hooks/useSEO";
import { BRAND_AUTH_LOGO_URL, BRAND_NAME, BRAND_PERSON_NAME, BRAND_SITE_URL } from "@/lib/brandConfig";
import { clearCumpleSession, loadCumpleSession } from "@/lib/cumpleSession";
import { normalizeCumpleChargeAmount, resolveCumpleMarket } from "@/lib/cumplePaymentConfig";
import { getCumpleSessionId, postCumpleSlackFunnel } from "@/lib/cumpleFunnel";
import {
  trackCumplePaymentSuccessPostHog,
  trackCumpleThankYouAnotherGiftPostHog,
  trackCumpleThankYouViewPostHog,
} from "@/lib/cumplePostHogAnalytics";
import { identifyDonorByEmailPostHog } from "@/lib/posthogAnalytics";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { useEffect, useMemo, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

const CUMPLE_BG_URL =
  "https://res.cloudinary.com/nm-creators/image/upload/v1776280287/P1140038_bd6t5x.jpg";

const COPY = {
  es: {
    title: "¡Gracias de corazón!",
    subtitle: "Aprecio muchísimo tu regalo",
    message:
      "Tu gesto me llegó profundo y me hace el día. Como agradecimiento, te voy a enviar una sorpresa especial muy pronto.",
    surprise: "🎁 Sorpresa en camino",
    surpriseHint: "Revisá tu email — ahí te aviso cuando esté lista.",
    yourMessage: "Tu mensaje",
    confirmation: (email: string) => `Confirmación enviada a ${email}`,
    anotherGift: "Enviar otro regalo de cumple",
    footer: "Con amor",
    home: "Volver al inicio",
  },
  en: {
    title: "Thank you from the bottom of my heart!",
    subtitle: "I truly appreciate your gift",
    message:
      "Your gesture means the world to me. As a thank-you, I'll be sending you a special surprise very soon.",
    surprise: "🎁 Surprise on the way",
    surpriseHint: "Keep an eye on your inbox — I'll let you know when it's ready.",
    yourMessage: "Your message",
    confirmation: (email: string) => `Confirmation sent to ${email}`,
    anotherGift: "Send another birthday gift",
    footer: "With love",
    home: "Back to home",
  },
} as const;

function isPaymentSuccess(sp: URLSearchParams): boolean {
  const status = sp.get("status");
  const checkout = sp.get("checkout");
  const redirectStatus = sp.get("redirect_status");
  return status === "success" || checkout === "success" || redirectStatus === "succeeded";
}

function resolveProvider(sp: URLSearchParams): "mercadopago" | "stripe" {
  const raw = sp.get("provider")?.toLowerCase().trim();
  if (raw === "mercadopago" || raw === "mp") return "mercadopago";
  if (
    sp.get("collection_id") ||
    sp.get("collection_status") ||
    sp.get("preference_id") ||
    sp.get("payment_id")
  ) {
    return "mercadopago";
  }
  return "stripe";
}

function Confetti() {
  const pieces = useMemo(
    () =>
      Array.from({ length: 48 }, (_, i) => ({
        id: i,
        x: Math.random() * 100,
        delay: Math.random() * 1.1,
        duration: 1.8 + Math.random() * 1.8,
        size: 5 + Math.random() * 10,
        color: ["#FBBF24", "#F59E0B", "#EC4899", "#8B5CF6", "#34D399", "#60A5FA", "#fff", "#FB923C"][
          Math.floor(Math.random() * 8)
        ],
        circle: Math.random() > 0.4,
        rotation: Math.random() * 720,
      })),
    [],
  );

  return (
    <div className="fixed inset-0 pointer-events-none z-30 overflow-hidden">
      {pieces.map((p) => (
        <motion.div
          key={p.id}
          style={{
            position: "absolute",
            top: "-5%",
            left: `${p.x}%`,
            width: p.size,
            height: p.size,
            backgroundColor: p.color,
            borderRadius: p.circle ? "50%" : 2,
          }}
          animate={{ y: "115vh", rotate: p.rotation, opacity: [1, 1, 1, 0] }}
          transition={{ duration: p.duration, delay: p.delay, ease: "linear" }}
        />
      ))}
    </div>
  );
}

function Background() {
  return (
    <>
      <motion.div
        initial={{ opacity: 0, scale: 1.06 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1.4 }}
        className="fixed inset-0 z-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url("${CUMPLE_BG_URL}")` }}
      />
      <div className="fixed inset-0 z-[1] bg-black/85 backdrop-blur-[6px]" />
      <div
        className="fixed inset-0 z-[2] pointer-events-none"
        style={{
          background:
            "linear-gradient(to bottom, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.2) 40%, rgba(0,0,0,0.2) 60%, rgba(0,0,0,0.6) 100%)",
        }}
      />
    </>
  );
}

export default function ThankYouALot() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const countryOverride = searchParams.get("country");
  const market = useMemo(() => resolveCumpleMarket(countryOverride), [countryOverride]);
  const t = COPY[market.language];
  const session = useMemo(() => loadCumpleSession(), []);
  const successTrackedRef = useRef(false);
  const thankYouViewTrackedRef = useRef(false);

  useSEO({
    title: market.language === "es" ? "¡Gracias por tu regalo!" : "Thank you for your gift!",
    description:
      market.language === "es"
        ? `Gracias por tu regalo de cumpleaños para ${BRAND_PERSON_NAME}.`
        : `Thank you for your birthday gift for ${BRAND_PERSON_NAME}.`,
    canonical: `${BRAND_SITE_URL}/thank-you-a-lot`,
  });

  useEffect(() => {
    if (thankYouViewTrackedRef.current) return;
    thankYouViewTrackedRef.current = true;
    trackCumpleThankYouViewPostHog(market, {
      from_payment_success: isPaymentSuccess(searchParams),
      payment_provider: isPaymentSuccess(searchParams)
        ? resolveProvider(searchParams)
        : undefined,
    });
  }, [market, searchParams]);

  useEffect(() => {
    if (!isPaymentSuccess(searchParams) || successTrackedRef.current) return;
    successTrackedRef.current = true;

    const formData = session?.formData;
    const donationId = session?.donationId ?? null;
    const raw = formData?.customAmount
      ? parseFloat(formData.customAmount)
      : formData?.selectedAmount
        ? parseFloat(formData.selectedAmount)
        : 0;
    const amount = normalizeCumpleChargeAmount(raw, market);
    const provider = resolveProvider(searchParams);
    const donorEmail = formData?.email.trim() || undefined;
    const paymentId =
      searchParams.get("payment_id") ||
      searchParams.get("collection_id") ||
      searchParams.get("preference_id") ||
      undefined;

    if (donorEmail) {
      identifyDonorByEmailPostHog(donorEmail, {
        funnel: "cumple",
        payment_provider: provider,
        last_donation_amount: amount,
        last_donation_currency: market.currency,
        ...(donationId ? { last_donation_id: donationId } : {}),
      });
    }

    trackCumplePaymentSuccessPostHog(market, amount, donationId, {
      payment_provider: provider,
      page_path: "/thank-you-a-lot",
      has_email: !!donorEmail,
      has_name: !!formData?.name.trim(),
      has_message: !!formData?.message.trim(),
      donor_email: donorEmail,
      payment_id: paymentId,
    });
    postCumpleSlackFunnel("cumple_payment_return_success", {
      sessionId: getCumpleSessionId(),
      lastStep: "thank_you",
      amount: amount > 0 ? amount : undefined,
      userEmail: formData?.email.trim() || undefined,
      currency: market.currency,
      country: market.country,
      checkoutGateway: market.gateway,
      extra: {
        donation_id: donationId ?? undefined,
        provider,
        page: "thank-you-a-lot",
      },
    });

    setSearchParams({}, { replace: true });
  }, [market, searchParams, session, setSearchParams]);

  const formData = session?.formData;

  return (
    <div className="min-h-[100dvh] min-h-[100svh] flex flex-col items-center justify-center p-5 relative overflow-y-auto overscroll-contain text-white font-body lining-nums tabular-nums">
      <Background />
      <Confetti />

      <motion.div
        initial={{ scale: 0.88, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: "spring", stiffness: 260, damping: 22 }}
        className="max-w-sm w-full space-y-5 relative z-20"
      >
        <div className="flex justify-center">
          <div className="relative">
            <motion.img
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.1, type: "spring" }}
              src={BRAND_AUTH_LOGO_URL}
              alt={BRAND_PERSON_NAME}
              className="w-20 h-20 rounded-full border-4 border-amber-400/40 shadow-2xl object-cover"
            />
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.5, type: "spring" }}
              className="absolute -bottom-1.5 -right-1.5 w-7 h-7 bg-green-500 rounded-full border-2 border-black flex items-center justify-center"
            >
              <Check size={14} strokeWidth={3} />
            </motion.div>
          </div>
        </div>

        <motion.div
          initial={{ y: 16, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.12 }}
          className="bg-gradient-to-br from-amber-500/20 to-pink-500/20 backdrop-blur-2xl rounded-3xl border-2 border-amber-400/40 p-7 text-center space-y-5 shadow-2xl"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.28, type: "spring", stiffness: 200 }}
            className="mx-auto w-20 h-20 bg-black/35 rounded-full flex items-center justify-center border border-white/10"
          >
            <span className="text-5xl" aria-hidden>
              🎂
            </span>
          </motion.div>

          <div className="space-y-2">
            <p className="text-amber-300 text-[10px] font-bold uppercase tracking-widest">{t.subtitle}</p>
            <h1 className="text-2xl font-black">{t.title}</h1>
            <p className="text-white/75 leading-relaxed text-sm">{t.message}</p>
          </div>

          <motion.div
            initial={{ y: 10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="bg-pink-500/10 border border-pink-500/25 rounded-2xl p-4 space-y-1.5"
          >
            <p className="text-pink-200 text-sm font-semibold">{t.surprise}</p>
            <p className="text-white/55 text-xs leading-relaxed">{t.surpriseHint}</p>
          </motion.div>

          {formData?.message && (
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.55 }}
              className="bg-amber-500/10 border border-amber-500/25 rounded-2xl p-4 text-center space-y-2"
            >
              <p className="text-amber-300 text-[10px] font-bold uppercase tracking-widest">
                🎉 {t.yourMessage}
              </p>
              <p className="text-white text-sm leading-relaxed font-medium">
                &ldquo;{formData.message}&rdquo;
              </p>
              {formData.name && <p className="text-white/45 text-xs italic">— {formData.name}</p>}
            </motion.div>
          )}

          {formData?.email && (
            <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl px-4 py-2.5 flex items-center justify-center gap-2">
              <Check size={14} className="text-blue-400" />
              <p className="text-blue-300 text-xs">{t.confirmation(formData.email)}</p>
            </div>
          )}

          <div className="space-y-3 pt-1">
            <Button
              onClick={() => {
                trackCumpleThankYouAnotherGiftPostHog(market);
                clearCumpleSession();
                navigate("/r/cumple");
              }}
              className="w-full h-12 bg-gradient-to-r from-amber-400 to-pink-400 text-black font-bold rounded-xl shadow-xl hover:from-amber-300 hover:to-pink-300 ui-transition"
            >
              🎁 {t.anotherGift}
            </Button>
            <Link
              to="/"
              className="block text-center text-white/45 text-xs hover:text-white/70 transition-colors"
            >
              {t.home}
            </Link>
          </div>
        </motion.div>

        <p className="text-center text-white/45 text-sm">
          {t.footer}, <span className="text-amber-400 font-medium">{BRAND_NAME}</span> 🎂
        </p>
      </motion.div>
    </div>
  );
}